import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { company, images } from "@/data/company";

const taglineWords = ["Build", "Connect", "Grow"];

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={images.heroBg}
          alt="Abstract technology network"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0a0f1e]/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1e] via-transparent to-[#0a0f1e]/70" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs uppercase tracking-[0.3em] text-[#22d3ee] font-bold mb-6"
        >
          {company.tagline}
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white max-w-4xl leading-[1.05]"
        >
          {company.heroHeadline}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed"
        >
          {company.heroSub}
        </motion.p>

        {/* Live tagline text */}
        <motion.div
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-8 flex items-center gap-4 flex-wrap"
        >
          {taglineWords.map((w, i) => (
            <div key={w} className="flex items-center gap-4">
              <span className="font-heading text-lg font-bold uppercase tracking-[0.15em] text-white">
                {w}
                <span className="text-[#22d3ee]">.</span>
              </span>
              {i < taglineWords.length - 1 && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#22d3ee]/60" />
              )}
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 bg-[#22d3ee] text-[#0a0f1e] font-bold px-8 py-4 rounded-sm hover:bg-[#67e8f9] transition-colors"
          >
            Start a project
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#services"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center border border-white/20 text-white font-bold px-8 py-4 rounded-sm hover:bg-white/5 transition-colors"
          >
            Explore services
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl border-t border-white/10 pt-10"
        >
          {company.stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.label}`}>
              <div className="font-heading text-3xl md:text-4xl font-black text-white">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
